import { ChartDataRequestModel } from "../../../../../pure-javascript-api/ti-js-api";
import { JSApiPointToTiIndicatorPointMapper } from "../../js-api-mappers/data-point/js-api-point-to-ti-indicator-point.mapper";
import { WebChartDataService } from "../../services/web-chart-data.service";
import { IChartContainer } from "../base/chart-container.interface";
import { TIChartPointModel } from "../data-point/ti-chart-point.model";
import { IndicatorColorType, TIIndicatorDataModel } from "../data-point/ti-indicator-data.model";
import { EMACloudSettingModel, EMACloudSettingsModel } from "../ema-cloud/ema-cloud-settings.model";
import { IndicatorType } from "../enum/indicator-type.enum";
import { ChartMode } from "../window-wrapper/marketing-webchart-window-wrapper.model";
import { ManageIndicatorModel } from "./manage-indicator.model";

export class WebChartIndicatorManager {

    private indicators: Array<ManageIndicatorModel> = [];

    private service: WebChartDataService;

    constructor(service: WebChartDataService) {
        this.service = service;
    }

    public mapTiIndicatorPoints(points: Array<TIChartPointModel>, emaCloudSettings: EMACloudSettingsModel): Array<TIIndicatorDataModel> {
        let tiIndicatorsData: Array<TIIndicatorDataModel> = [];

        for (const indicator of this.indicators) {
            if (indicator.highChartIndicator) {
                const ind = new TIIndicatorDataModel({
                    highChartIndicator: indicator.highChartIndicator,
                    name: indicator.name,
                    abbreviatedName: indicator.name,
                    chartAreaName: indicator.targetChartArea,
                    color: indicator.colorOne,
                    indicatorType: indicator.indicatorType,
                    colorType: indicator.colorTypeOne
                });

                ind.chartType = 'line';
                if (indicator.indicatorType == IndicatorType.HighChartStochastic) {
                    const name = indicator.name;
                    const childInd =  new TIIndicatorDataModel({
                        highChartIndicator: indicator.highChartIndicator,
                        name: name,
                        abbreviatedName: name,
                        chartAreaName: indicator.targetChartArea,
                        color: indicator.colorTwo,
                        indicatorType: indicator.indicatorType,
                        colorType: indicator.colorTypeTwo
                    });
                    childInd.chartType = 'line';

                    ind.childIndicators = [childInd];
                }

                tiIndicatorsData.push(ind);
            } else {
                tiIndicatorsData = tiIndicatorsData.concat(JSApiPointToTiIndicatorPointMapper.map(points, indicator));
            }
        }

        if (emaCloudSettings) {
            const emaCloudDataModel = JSApiPointToTiIndicatorPointMapper.mapEMACloud(points, emaCloudSettings);
            tiIndicatorsData = tiIndicatorsData.concat(emaCloudDataModel);
        }

        return tiIndicatorsData;
    }

    public setChartDataRequestIndicators(chartDataRequest: ChartDataRequestModel, emaClondSettings: Array<EMACloudSettingModel>) {
        if (emaClondSettings.length) {
            for (const setting of emaClondSettings) {
                if (!setting.active) continue;

                const value = Math.max(setting.fast, setting.slow) * 2;
                if (value > chartDataRequest.extraRowCount) {
                    chartDataRequest.extraRowCount = value;
                }

                const fastKey = setting.getFastGridRequestKey();
                if (!chartDataRequest.gridRequest.some(x => x[0] == fastKey)) {
                    chartDataRequest.gridRequest.push([fastKey, []]);
                }

                const slowKey = setting.getSlowGridRequestKey();
                if (!chartDataRequest.gridRequest.some(x => x[0] == slowKey)) {
                    chartDataRequest.gridRequest.push([slowKey, []]);
                }
            }
        }

        this.indicators = this.getSavedIndicatorsForIndicatorManager();
        for (const indicator of this.indicators) {
            if (indicator.highChartIndicator) continue;

            indicator.gridData = this.getIndicatorGridRequest(indicator);
            for (const g of indicator.gridData) {
                if (g?.[0] && !chartDataRequest.gridRequest.some(x => x[0] == g?.[0])) {
                    chartDataRequest.gridRequest.push(g);
                }
            }

            const value = Math.max(indicator.valueOne, indicator.valueTwo);
            if (value > chartDataRequest.extraRowCount) {
                chartDataRequest.extraRowCount = value;
            }
        }
    }

    public requestSaveIndicatorsForIndicatorManager(items: Array<ManageIndicatorModel>) {
        const key = this.getIndicatorsForIndicatorManagerKeyStorage();
        try {
            const jsonValue = JSON.stringify(items);
            localStorage.setItem(key, jsonValue);
        } catch { }
    }

    public getSavedIndicatorsForIndicatorManager(displayVolumeIndicator?: boolean): Array<ManageIndicatorModel> {
        if (this.service.chartMode != ChartMode.Standard || this.service.isPnP) {
            return [];
        }

        let result: Array<ManageIndicatorModel> = [];
        const key = this.getIndicatorsForIndicatorManagerKeyStorage();
        try {
            const items = localStorage.getItem(key);
            if (items) {
                result = JSON.parse(items);
            } else {
                if (displayVolumeIndicator && !result.some(x => x.indicatorType == IndicatorType.Volume)) {
                    result.unshift(new ManageIndicatorModel({
                        name: `Volume`,
                        targetChartArea: `ChartArea2`,
                        indicatorType: IndicatorType.Volume,
                        colorTypeOne: IndicatorColorType.UpDown,
                        upColorOne: `#008000`,
                        downColorOne: `#ff0000`
                    }));

                    this.requestSaveIndicatorsForIndicatorManager(result);
                }
            }
        } catch { }

        return result;
    }

    private getIndicatorGridRequest(model: ManageIndicatorModel): any {
        const type = model.indicatorType;
        if (type == IndicatorType.Volume) {
            return [['VOLUME', []]];
        } else if (type == IndicatorType.AverageVolume) {
            return [['VOLUME', []], ['AVG_VOLUME', [`average(${model.valueOne}, 1, "VOLUME", 1)`]]];
        } else if (type == IndicatorType.AverageDirectionIndex) {
            return [['ADX', []]];
        } else if (type == IndicatorType.AverageTrueRange) {
            return [[`ATR ${model.valueOne}`, []]];
        } else if (type == IndicatorType.RSI) {
            return [[`RSI ${model.valueOne} CLOSE`, []]];
        } else if (type == IndicatorType.MACD) {
            return [
                [`MACD_LINE ${model.valueOne} ${model.valueTwo}`, []],
                [`MACD_SIGNAL_LINE ${model.valueOne}`, []],
                [`MACD_HIST ${model.valueOne} ${model.valueTwo} 9`, []],
            ];
        } else if (type == IndicatorType.DonchianChanel) {
            return [
                [`DC TOP`, [`max_val(${model.valueOne}, 1, "HIGH")`]],
                [`DC BOTTOM`, [`min_val(${model.valueOne}, 1, "LOW")`]],
            ];
        } else if (type == IndicatorType.MoneyFlowIndex) {
            return [
                [`MONEY_FLOW_INDEX ${model.valueOne}`, []],
            ];
        } else if (type == IndicatorType.MovingAverageEMA) {
            return [
                [`EMA ${model.valueOne} CLOSE`, []],
            ];
        } else if (type == IndicatorType.MovingAverageSMA) {
            return [
                [`SMA ${model.valueOne} CLOSE`, []],
            ];
        } else if (type == IndicatorType.MultiPeriodHigh) {
            return [
                [`MP HIGH`, [`max_val(${model.valueOne}, 1, "HIGH")`]],
            ];
        } else if (type == IndicatorType.MultiPeriodLow) {
            return [
                [`MP LOW`, [`min_val(${model.valueOne}, 1, "LOW")`]],
            ];
        } else if (type == IndicatorType.PivotPoint) {
            return [
                [`Pivot Point (P)`, [`((ref(0,"YESTERDAY_OHLC CLOSE") + ref(0,"YESTERDAY_OHLC LOW") + ref(0,"YESTERDAY_OHLC HIGH")) / 3 )`]],
            ];
        } else if (type == IndicatorType.RateOfChange) {
            return [
                [`ROC ${model.valueOne}`, [`(ref(0,"CLOSE") - ref(${model.valueOne},"CLOSE")) / ref(${model.valueOne},"CLOSE") * 100`]],
            ];
        } else if (type == IndicatorType.RelativeVolume) {
            return [
                [`REL_VOL`, [``]],
            ];
        } else if (type == IndicatorType.DistanceFromEMA) {
            return [
                [`Distance from EMA`, [`(ref(0,"CLOSE") - ref(0,"EMA 8 CLOSE")) / ref(0,"CLOSE") *100`]],
            ];
        } else if (type == IndicatorType.Stochastics) {
            return [
                [`STOCH_K ${model.valueOne}`, [``]],
            ];
        } else if (type == IndicatorType.TIPatternRec) {
            return [
                [`PAT_REC_INDEX ${model.valueOne}`, [``]],
            ];
        } else if (type == IndicatorType.TIStrength) {
            return [
                [`TI_SCORE`, [``]],
            ];
        } else if (type == IndicatorType.VWAP) {
            return [
                [`VWAP`, [``]],
            ];
        } else if (type == IndicatorType.WilliamR) {
            return [
                [`Williams %R ${model.valueOne.toFixed(2)}`, [`(max_val(${model.valueOne}, 0, "HIGH", 1)-ref(0,"CLOSE")) / (max_val(${model.valueOne}, 0, "HIGH", 1)- min_val(${model.valueOne}, 0, "LOW", 1)) * -100`]],
            ];
        } else if (type == IndicatorType.DollarVolume) {
            return [
                ['VOLUME', []],
                [`Dollar Volume`, [`((ref(0,"CLOSE") + ref(0,"LOW") + ref(0,"HIGH")) / 3 ) * ref(0,"VOLUME")`]],
            ];
        } else if (type == IndicatorType.BBands) {
            return [
                [`BB_TOP ${model.valueOne} ${model.valueTwo.toFixed(2)}`, []],
                [`BB_BOTTOM ${model.valueOne} ${model.valueTwo.toFixed(2)}`, []],
            ];
        } else if (type == IndicatorType.BBWidth) {
            return [
                [`BB_WIDTH ${model.valueOne} ${model.valueTwo}`, [``]],
            ];
        } else if (type == IndicatorType.Drawdown) {
            return [
                [`Drawdown`, [`(max_val(252,0,"HIGH") - ref(0,"CLOSE") ) / max_val(252,0,"HIGH") *-100`]]
            ]
        } else if (type == IndicatorType.KeltnerChannel) {
            return [
                [`KELTNER_TOP 10`, []],
                [`KELTNER_BOTTOM 10`, []]
            ]
        }
        
        return [];
    }
    
    private getIndicatorsForIndicatorManagerKeyStorage(): string {
        return 'INDICATORS_MANAGER_ITEMS';
    }
}